import { Breakpoint } from '@mui/material';
import { StoreApi, useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useTheme } from '../useTheme';
import { BreakpointsStoreReady } from './breakpoint-provider-model';
import { useElementBreakpointsContext, useElementBreakpointsStore } from './ElementBreakpointsProvider';
import { useViewportBreakpointsStore } from './ViewportBreakpointsProvider';

/**
 * Returns an array of breakpoint keys ordered from smallest to largest.
 */
export const useBreakpointKeys = () => {
  const { theme } = useTheme();

  return theme.breakpoints.keys;
};

/**
 * Which context provider that should be used to evaluate breakpoints.
 * - `viewport`: Use the viewport-based breakpoints from the `ViewportBreakpointsProvider`.
 * - `element`: Use the element-based breakpoints from `ElementBreakpointsProvider`.
 */
export type BreakpointStateSource = 'viewport' | 'element';

export const DEFAULT_BREAKPOINT_STATE_SOURCE: BreakpointStateSource = 'element';

export interface UseBreakpointStateOpts {
  /**
   * Which context provider that should be used to evaluate breakpoints.
   * - `viewport`: Use the viewport-based breakpoints from the `ViewportBreakpointsProvider`.
   * - `element`: Use the element-based breakpoints from `ElementBreakpointsProvider`.
   *
   * @default 'element'
   */
  breakpointSource?: BreakpointStateSource;
}

/**
 * Retrieves the correct breakpoints store based on the requested source.
 */
export const useBreakpointsStore = (
  breakpointSource: BreakpointStateSource = DEFAULT_BREAKPOINT_STATE_SOURCE
): StoreApi<BreakpointsStoreReady> => {
  const viewportStore = useViewportBreakpointsStore();
  const elementStore = useElementBreakpointsStore();

  let store = breakpointSource === 'element' ? elementStore : viewportStore;

  useStore(store, (state) => {
    if (state.isLoading) {
      if (process.env.NODE_ENV === 'production') {
        // In production use cases, if something goes wrong, fall back to the viewport store, which is always
        // loaded. This is not ideal, but much better than throwing an error and potentially breaking the whole page.
        // eslint-disable-next-line react-hooks/immutability
        store = viewportStore;
      } else {
        // In non-prod use cases, throw an error to let devs know they misconfigured something, so that hopefully
        // the fallback code above is never used.
        throw new Error(
          `Attempting to use a ${breakpointSource} breakpoint store before it is finished loading. Ensure that associated context provider and its children have been properly configured.`
        );
      }
    }
  });

  // The check above validates the store is ready (but isn't something TS can understand to automatically adjust the
  // type), so we can safely cast as a BreakpointsStoreReady, which makes it easier for all the consuming hooks to
  // work with the store.
  return store as StoreApi<BreakpointsStoreReady>;
};

export interface UseConfigureElementBreakpointsResponse {
  /**
   * When true, the breakpoints store is loading. Make sure to wait for `isLoading` to be `false` before rendering
   * children that depend on the `ElementBreakpointsProvider` as a "breakpoint source." If you do not do so, they will
   * throw an error.
   */
  isLoading: boolean;

  /**
   * Pass this ref to the html element you want to measure for the `ElementBreakpointsProvider`. In most cases, this
   * should be handled by the `ProtectedApp` component in `cloud`. If you are using this for drawers or other use cases
   * that are not the primary page content, make sure you set up a separate `ElementBreakpointsProvider` for those
   * sections of the page to avoid accidentally overriding the ref for the main page content.
   */
  ref: React.RefCallback<HTMLDivElement>;
}

/**
 * Hook necessary to to set up element-based breakpoints via `ElementBreakpointsProvider`.
 */
export const useConfigureElementBreakpoints = (): UseConfigureElementBreakpointsResponse => {
  const { ref, store } = useElementBreakpointsContext();
  const isLoading = useStore(store).isLoading;

  return {
    ref,
    isLoading,
  };
};

/**
 * Hook that takes an array of breakpoints and returns a mapping of those breakpoints to a boolean indicating if they
 * are currently active or not.
 *
 * This hook enables the up/down/between hooks to limit the numbers of re-renders they trigger to just the relevant
 * breakpoints.
 */
const useBreakpointMatches = <T extends Breakpoint[]>(
  breakpoints: T,
  opts?: UseBreakpointStateOpts
): { [key in T[number]]: boolean } => {
  const store = useBreakpointsStore(opts?.breakpointSource);

  return useStore(
    store,
    useShallow((state) => {
      return breakpoints.reduce(
        (result, breakpoint) => {
          return {
            ...result,
            [breakpoint]: state[breakpoint],
          };
        },
        {} as { [key in T[number]]: boolean }
      );
    })
  );
};

/**
 * Returns the current active breakpoint.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders at each breakpoint change.
 */
export const useActiveBreakpoint = (opts?: UseBreakpointStateOpts): Breakpoint => {
  const store = useBreakpointsStore(opts?.breakpointSource);

  return useStore(store, (state) => {
    return state.activeBreakpoint;
  });
};

/**
 * Returns true if the current screen width matches the specified breakpoint key.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders when the match status changes for the specified breakpoint.
 */
export const useBreakpoint = (breakpoint: Breakpoint, opts?: UseBreakpointStateOpts): boolean => {
  const store = useBreakpointsStore(opts?.breakpointSource);

  return useStore(store, (state) => {
    return state[breakpoint];
  });
};

/**
 * Returns true if the current screen width is greater than the screen size given by the breakpoint key (inclusive).
 *
 * This hook provides a JS-based api similar similar to MUI's CSS-based `theme.breakpoints.up` api.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders when the match status changes for the specified breakpoint upward (inclusive).
 */
export const useBreakpointUp = (breakpoint: Breakpoint, opts?: UseBreakpointStateOpts): boolean => {
  const breakpointKeys = useBreakpointKeys();
  const indexOfRequestedBreakpoint = breakpointKeys.indexOf(breakpoint);
  const breakpointsToCheck = breakpointKeys.slice(indexOfRequestedBreakpoint);
  const breakpointMatches = useBreakpointMatches(breakpointsToCheck, opts);

  return Object.values(breakpointMatches).some((matches) => matches);
};

/**
 * Returns true if the current screen width is less than the screen size given by the breakpoint key (exclusive).
 *
 * This function provides a JS-based api similar similar to MUI's CSS-based `theme.breakpoints.down` api.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders when the match status changes for the specified breakpoint downward (exclusive).
 */
export const useBreakpointDown = (breakpoint: Breakpoint, opts?: UseBreakpointStateOpts): boolean => {
  const breakpointKeys = useBreakpointKeys();
  const indexOfRequestedBreakpoint = breakpointKeys.indexOf(breakpoint);
  const breakpointsToCheck = breakpointKeys.slice(0, indexOfRequestedBreakpoint);
  const breakpointMatches = useBreakpointMatches(breakpointsToCheck, opts);

  return Object.values(breakpointMatches).some((matches) => matches);
};

/**
 * Returns true if the current screen width is greater than the screen size given by start breakpoint key (inclusive)
 * and less than the screen size given by the end breakpoint key (exclusive).
 *
 * This function provides a JS-based api similar similar to MUI's CSS-based `theme.breakpoints.between` api.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders when the match status changes for the impacted breakpoints
 */
export const useBreakpointBetween = (
  startBreakpoint: Breakpoint,
  endBreakpoint: Breakpoint,
  opts?: UseBreakpointStateOpts
): boolean => {
  const breakpointKeys = useBreakpointKeys();
  const indexOfStartBreakpoint = breakpointKeys.indexOf(startBreakpoint);
  const indexOfEndBreakpoint = breakpointKeys.indexOf(endBreakpoint);
  const breakpointsToCheck = breakpointKeys.slice(indexOfStartBreakpoint, indexOfEndBreakpoint);
  const breakpointMatches = useBreakpointMatches(breakpointsToCheck, opts);

  return Object.values(breakpointMatches).some((matches) => matches);
};

/**
 * Returns true if the current screen width should be treated as mobile.
 *
 * Currently the "mobile" shorthand maps to the `xs` and `sm` breakpoints.
 *
 * Note that this utility uses the element-based breakpoint source by default (usually configured to the
 * main content on the page). Set the `breakpointSource` option to `viewport` to get viewport-based
 * breakpoint results.
 *
 * This hook will trigger re-renders when the isMobile state changes.
 */
export const useIsMobile = (opts?: UseBreakpointStateOpts): boolean => {
  const store = useBreakpointsStore(opts?.breakpointSource);

  return useStore(store, (state) => {
    return state.isMobile;
  });
};
